/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React from "react";
import { IconButton } from "./Icons";

const headerStyle = (selectedConfig, transparentBackground, backgroundImageCalcResults) => {
	const backgroundImageSettings = !transparentBackground
		? !backgroundImageCalcResults
			? {
					backgroundImage: selectedConfig.headerBackgroundImage ? `url(${selectedConfig.headerBackgroundImage})` : null,
					backgroundSize: "cover",
					backgroundPosition: "center center",
					backgroundRepeat: "no-repeat",
			  }
			: {
					backgroundImage: `url(${selectedConfig.headerBackgroundImage})`,
					backgroundSize: `${backgroundImageCalcResults.scaledWidth}px ${backgroundImageCalcResults.scaledHeight}px`,
					backgroundPosition: `calc(50% + ${backgroundImageCalcResults.offsets.x}px) -${backgroundImageCalcResults.offsets.y}px`,
					backgroundRepeat: "no-repeat",
			  }
		: {};

	return {
		backgroundColor: !transparentBackground ? selectedConfig.headerBackgroundColor || "#941012" : undefined,
		borderRadius: "5px 5px 0 0",
		padding: "10px",
		display: "flex",
		flexDirection: "column",
		flex: "none",
		minHeight: "90px",
		...backgroundImageSettings,
	};
};

const titleStyle = (selectedConfig) => {
	return {
		fontSize: "20px",
		color: selectedConfig.headerTextColor || "#fff",
		margin: "8px 0",
		textAlign: "center",
		minHeight: "22px",
		wordBreak: "break-word",
		overflowWrap: "break-word",
	};
};

const languageSelectorButtonStyle = (selectedConfig) => {
	return {
		color: selectedConfig.languageTextColor || "#fff",
		border: "none",
		background: "transparent",
		cursor: "pointer",
		marginLeft: "-5px",
	};
};

const languageSelectorStyle = () => {
	return {
		position: "relative",
		display: "flex",
		verticalAlign: "middle",
		height: "24px",
		cursor: "pointer",
		border: "none",
		alignItems: "center",
	};
};

export const ChatHeaderDemoDisplay = ({
	isChatWidgetMaximized,
	title,
	isMuted,
	selectedConfig,
	transparentBackground,
}) => {
	const [backgroundImageCalcResults, setBackgroundImageCalcResults] = React.useState(undefined);

	React.useEffect(() => {
		if (!transparentBackground && selectedConfig.headerBackgroundImage) {
			const headerImage = new Image();
			const cropSettings = selectedConfig.headerImageCropSettings?.croppedArea;
			headerImage.onload = ({ target: img }) => {
				if (cropSettings) {
					const dimensions = { height: img.naturalHeight, width: img.naturalWidth };

					// Calculating offset from center that background image should be placed at on the X axis.
					// cropSettings.x = The number of pixels from the left side of the image to the left side of the selected display area
					// cropSettings.width = The width of the display area.
					// cropSettings.zoom = The amount the image will be scaled.
					const xScaled = cropSettings.x * cropSettings.zoom;
					const halfWidthScaled = (cropSettings.width * cropSettings.zoom) / 2;
					const xCenterOfScaledDisplayArea = xScaled + halfWidthScaled;
					const xCenterOfScaledImage = (dimensions.width * cropSettings.zoom) / 2;
					setBackgroundImageCalcResults({
						scaledWidth: cropSettings.zoom * dimensions.width,
						scaledHeight: cropSettings.zoom * dimensions.height,
						offsets: { y: cropSettings.zoom * cropSettings.y, x: xCenterOfScaledImage - xCenterOfScaledDisplayArea },
					});
				}
			};
			headerImage.src = selectedConfig.headerBackgroundImage;
		}
	}, [selectedConfig.headerImageCropSettings, selectedConfig.headerBackgroundImage, transparentBackground]);

	const defaultBucketLink = selectedConfig.defaultBucketLink;
	let closeImage = selectedConfig.closeButtonImage || `${defaultBucketLink}/CloseSymbolGray.svg`;
	let hideImage = selectedConfig.hideButtonImage || `${defaultBucketLink}/HideSymbolGray.svg`;
	let languageImage = selectedConfig.languageButtonImageURL || `${defaultBucketLink}/LanguageSymbolGray.svg`;
	let expandImage = selectedConfig.expandButtonImage || `${defaultBucketLink}/expandSymbolGray.svg`;
	let collapseImage = selectedConfig.collapseExpandButtonImage || `${defaultBucketLink}/collapseSymbolGray.svg`;
	let muteImage = selectedConfig.muteMenuButtonImage || `${defaultBucketLink}/MuteMenuButtonImage.svg`;
	let unmuteImage = selectedConfig.unmuteMenuButtonImage || `${defaultBucketLink}/UnMuteMenuButtonImage.svg`;
	let downloadImage = selectedConfig.downloadButtonImageURL || `${defaultBucketLink}/DownloadSymbolGray.svg`;
	let moreOptionsImage = selectedConfig.escalationOptionsImageURL || `${defaultBucketLink}/moreOptions.svg`;

	return (
		<div style={headerStyle(selectedConfig, transparentBackground, backgroundImageCalcResults)}>
			<div style={{ display: "flex", justifyContent: "space-between" }}>
				<div style={{ display: "flex", alignItems: "center" }}>
					{selectedConfig.enableAudioNotification && (
						<IconButton title={"Unmute Notification"} iconUrl={isMuted ? unmuteImage : muteImage} />
					)}

					{selectedConfig.enableDownloadButton && <IconButton title="Download Transcript" iconUrl={downloadImage} />}

					{selectedConfig.enableTranslation && (
						<div style={languageSelectorStyle()}>
							<IconButton iconUrl={languageImage} />
							<button type="button" style={languageSelectorButtonStyle(selectedConfig)}>
								English
							</button>
						</div>
					)}
				</div>
				<div style={{ display: "flex", alignItems: "center" }}>
					<IconButton
						title={isChatWidgetMaximized ? "Collapse" : "Expand"}
						iconUrl={isChatWidgetMaximized ? collapseImage : expandImage}
					/>
					{selectedConfig.enableChatEscalations && <IconButton iconUrl={moreOptionsImage} />}
					<IconButton title="Hide" iconUrl={hideImage} />
					<IconButton title="Close" iconUrl={closeImage} />
				</div>
			</div>

			<div style={titleStyle(selectedConfig)}>{title}</div>
		</div>
	);
};
